import React, { useEffect, useState } from 'react'
import ActionArea from '../../components/ActionArea'
import SmartSearch from '../../components/UI/SmartSearch'
import { useLoading } from '../../context/LoadingProvider/useLoading'
import { IListPosIpadModel, IPosBundleServer, IPosBundleServerWithoutCompatible } from './types'
import {disableIncompatiblePosBlundleServerList, getPosBlundleServerList, updatePosBlundleServerList} from '../../services/posBundleServer'
import SwitchInput from '../../components/UI/SwitchInput'
import List from '../../components/List'
import Button from '../../components/UI/Button'
import FloatingButton from '../../components/UI/FloatingButton'
import { sendSystemLog, useAuth } from '../../context/AuthProvider/useAuth'

let posBundleListInitial: IPosBundleServer[] = []

function index() {
  const {canEdit} = useAuth()
  const [posBundleList, setPosBundleList] = useState<IPosBundleServer[]>([])
  const loading = useLoading()
  const [search, setSearch] = useState({
    text: '',
    obj: {
      id: '',
      name: '',
      type: ''
    }
  })
  const [validSearch, setValidSearch] = useState(false)
  const log = {
    log_type: 'visited',
    log_object: 'pos_bundle_server_management'
  }

  const handleEnter = (event: KeyboardEvent) => {
    if (loading.loading) return;
    if (!event.repeat && event.key === 'Enter') {
      onRefresh()
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleEnter)
    return () => document.removeEventListener("keydown", handleEnter)
  })

  useEffect(() => {
    sendSystemLog(log);
  }, [])

  const validateCompatibility = (bundleList: IPosBundleServerWithoutCompatible[]) => {
    return bundleList.map((bundle) => {
      if(bundle.listPosIpadModel.find((model) => model.compatible === false || !model.compatible)) return {...bundle, compatible: false}
      return {...bundle, compatible: true}
    })
  }

  const onRefresh = async () => {
    loading.addLoading()
    let params = {
      [`${search.obj.type}Id`]: search.obj.id,
    }
    const res = await getPosBlundleServerList(params)
    posBundleListInitial = validateCompatibility(await getPosBlundleServerList(params))
    setPosBundleList(validateCompatibility(res))
    loading.removeLoading()
  }

  const onCheckChange = (index: number, value: boolean) => {
      let pos = posBundleList
      pos[index].posBundleServerEnable = value
      setPosBundleList([...pos])
  }

  const getModelVariant = (value: string, isCompatible: boolean) => {
    if(value === 'unknown') return '--unknown' 
    if(isCompatible) return '--compatible'
    return ''
  }

  const checkControl = (type: 'enable' | 'disable' | 'disableIncompatible') => {
    let pos = posBundleList
    if(type === 'enable' || type === 'disable') {

      pos = pos.map((o) => {
        return {...o, posBundleServerEnable: type === 'enable' ? true : false}
      })
    }
    if(type === 'disableIncompatible') {
      pos = pos.map((o) => {
        const incompatible = o.listPosIpadModel.find(i => i.compatible === false)
        if(incompatible)  {
          return {...o, posBundleServerEnable: false }
        }
        return o
      })
    }
    setPosBundleList([...pos])
  }

  const onSave = async () => {
    const compatibleBundleList: {id: string, posBundleServerEnable: boolean}[] = [];
    const incompatibleBundleList: string[] = [];
    posBundleList.forEach((bundle) => {
      if(!bundle.compatible && !bundle.posBundleServerEnable) return incompatibleBundleList.push(bundle.id)
      compatibleBundleList.push({id: bundle.id, posBundleServerEnable: bundle.posBundleServerEnable})
    })
    posBundleList
    loading.addLoading()
    if(compatibleBundleList.length > 0) await updatePosBlundleServerList(compatibleBundleList)
    if(incompatibleBundleList.length > 0) await disableIncompatiblePosBlundleServerList(incompatibleBundleList) 
    await onRefresh()
    loading.removeLoading()
  }

  return (
    <div className="pos-bundle-server-management">
      <FloatingButton text="Save" onClick={onSave} disabled={(JSON.stringify(posBundleList) === JSON.stringify(posBundleListInitial))|| !canEdit}/>
      <ActionArea buttonText={'Search'} buttonAction={onRefresh} disabled={!validSearch}>
        <SmartSearch
          label="Smart Search"
          searchBy={['distributor', 'charity', 'location']}
          value={search.text}
          status='active'
          onChange={(e: string) => setSearch({...search, text: e})}
          onObjValueChange={(e: any) => setSearch({...search, obj: e})}
          onValidSearch={setValidSearch}
          autoFocus
        />
      </ActionArea>
      {posBundleList.length > 0 && (
        <ActionArea style={{paddingBottom: 15}}>
          <>
            <Button 
              type="primary" 
              style={{marginRight: 20}} 
              onClick={() => checkControl('enable')}
              disabled={!canEdit}
            >
              <p>Enable All</p>
            </Button>
            <Button 
              type="primary" 
              style={{ backgroundColor: '#FD841F', marginRight: 20}} 
              onClick={() => checkControl('disableIncompatible')}
              disabled={!canEdit}
            >
              <p>Disable All With Incompatible POS</p>
            </Button>
            <Button 
              type="primary"
              style={{ backgroundColor: '#bd4247', marginRight: 20}} 
              onClick={() => checkControl('disable')}
              disabled={!canEdit}
            >
              <p>Disable All</p>
            </Button>
          </>
        </ActionArea>
      )}
      {posBundleList.length > 0 && posBundleList.map((o, i) => (
        <div key={o.name} className="pos-bundle-server-management__table">
          <div className="pos-bundle-server-management__table__header">
            <h3>{o.name}</h3>
            <div className="pos-bundle-server-management__table__enable">
              <SwitchInput
                onChange={(e: boolean) => onCheckChange(i, e)}
                options={{ check: true, uncheck: false }}
                value={o.posBundleServerEnable}
                label="Enable POS Bundle Server"
                disabled={!canEdit}
              />
            </div>
          </div>
          <List
            columns={[
              {
                title: 'Identifier',
                dataIndex: 'identifier',
                key: 'identifier',
                width: 150,
                render: (value: string) => value,
              },
              {
                title: 'Model',
                dataIndex: 'model',
                key: 'model',
                width: 150,
                render: (value: string, record: IListPosIpadModel ) => 
                <div className={`pos-bundle-server-management__table__model${getModelVariant(value, record.compatible)}`}>
                  {value}
                </div>,
              },
            ]}
            rows={o.listPosIpadModel}
          />
        </div>
      ))}

    </div>
  )
}

export default index